import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Button,
  Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import apiClient from '../utils/axios';

const Account = () => {
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [holdingsError, setHoldingsError] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchAccountInfo();
    }
  }, []);

  const fetchAccountInfo = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      // 계좌 요약 (kt00018) + 예수금 (kt00001)
      const [accountSettled, depositSettled] = await Promise.allSettled([
        apiClient.get('/account/info'),
        apiClient.get('/account/deposit'),
      ]);
      if (accountSettled.status !== 'fulfilled') {
        throw accountSettled.reason;
      }
      const accountResponse = { data: accountSettled.value.data };
      const depositData =
        depositSettled.status === 'fulfilled' ? depositSettled.value.data || null : null;
      console.log('계좌 정보 응답:', accountResponse.data);
      if (depositData) console.log('예수금 응답:', depositData);
      
      // 보유 종목 조회 (kt00004 - 계좌평가현황요청)
      // 참고: kt00004는 계좌 평가 현황과 함께 보유 종목 정보를 제공합니다.
      let holdingsData = null;
      try {
        const holdingsResponse = await apiClient.get('/account/holdings');
        console.log('보유 종목 응답 (전체):', holdingsResponse.data);
        console.log('보유 종목 응답 타입:', typeof holdingsResponse.data);
        console.log('보유 종목 응답 키들:', Object.keys(holdingsResponse.data || {}));
        
        // kt00004 응답 구조에 따라 데이터 추출
        // kt00004는 계좌 평가 현황과 함께 보유 종목 정보를 반환
        // 일반적으로 output 또는 output1 필드에 보유 종목 배열이 있음
        const rawData = holdingsResponse.data;
        if (rawData) {
          // kt00004의 보유 종목 필드들 (다양한 가능성 확인)
          // stk_acnt_evlt_prst는 주식계좌평가현황 배열 (보유 종목 정보)
          holdingsData = rawData.stk_acnt_evlt_prst || // 주식계좌평가현황 (보유 종목 배열)
                        rawData.output || 
                        rawData.output1 || 
                        rawData.acnt_evlt_remn_indv_tot || // 계좌평가잔고개별합계
                        rawData.stk_cntr_remn || // kt00005에서 사용하는 필드 (혹시 모를 경우)
                        rawData.data ||
                        rawData.holdings ||
                        (Array.isArray(rawData) ? rawData : null);
          
          console.log('추출된 보유 종목 데이터:', holdingsData);
          console.log('보유 종목 데이터 타입:', typeof holdingsData);
          console.log('보유 종목 데이터 배열 여부:', Array.isArray(holdingsData));
          
          // 배열 안에 배열이 있을 수 있으므로 평탄화
          if (Array.isArray(holdingsData)) {
            console.log('보유 종목 배열 길이:', holdingsData.length);
            if (holdingsData.length > 0) {
              console.log('첫 번째 요소 타입:', typeof holdingsData[0]);
              console.log('첫 번째 요소 배열 여부:', Array.isArray(holdingsData[0]));
              
              // 배열 안에 배열이 있는 경우 평탄화
              if (Array.isArray(holdingsData[0])) {
                console.log('중첩 배열 발견! 평탄화 전:', holdingsData);
                holdingsData = holdingsData.flat();
                console.log('평탄화 후 보유 종목 배열 길이:', holdingsData.length);
              }
              
              if (holdingsData.length > 0) {
                // 첫 번째 보유 종목의 전체 구조를 JSON으로 출력
                console.log('첫 번째 보유 종목 전체 구조:', JSON.stringify(holdingsData[0], null, 2));
                console.log('첫 번째 보유 종목 필드들:', Object.keys(holdingsData[0] || {}));
                
                // 모든 보유 종목의 필드명 확인
                const allFields = new Set();
                holdingsData.forEach((item, idx) => {
                  if (item && typeof item === 'object') {
                    Object.keys(item).forEach(key => allFields.add(key));
                    if (idx < 3) {
                      console.log(`보유 종목 ${idx} 전체 구조:`, JSON.stringify(item, null, 2));
                    }
                  }
                });
                console.log('모든 보유 종목에서 발견된 필드명:', Array.from(allFields));
              }
            }
          } else if (holdingsData && typeof holdingsData === 'object') {
            console.log('보유 종목 객체의 키들:', Object.keys(holdingsData));
          }
        }
      } catch (holdingsError) {
        console.error('보유 종목 조회 실패:', holdingsError);
        console.error('보유 종목 조회 실패 상세:', {
          message: holdingsError.message,
          response: holdingsError.response?.data,
          status: holdingsError.response?.status
        });
        
        // 보유 종목 조회 실패 시 에러 메시지 저장 (계좌 정보는 계속 표시)
        if (holdingsError.response?.data?.error) {
          setHoldingsError(holdingsError.response.data.error);
        } else if (holdingsError.message) {
          setHoldingsError(holdingsError.message);
      } else {
          setHoldingsError('보유 종목 조회에 실패했습니다.');
        }
      }
      
      // 계좌 정보와 보유 종목 정보 결합
      const combinedData = {
        ...accountResponse.data,
        entr: depositData?.entr,
        ord_alow_amt: depositData?.ord_alow_amt,
        pymn_alow_amt: depositData?.pymn_alow_amt,
        d2_entra: depositData?.d2_entra,
        krDeposit: depositData,
        holdings: holdingsData // 보유 종목 데이터를 별도 필드로 추가
      };
      
      console.log('최종 결합된 데이터:', combinedData);
      setAccountInfo(combinedData);
    } catch (error) {
      console.error('계좌 정보 조회 실패:', error);
      
      if (error.response?.status === 401) {
        setErrorMessage('로그인이 필요합니다.');
      } else if (error.response?.status === 400 && error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage('계좌 정보를 불러오는 중 오류가 발생했습니다.');
      }
      setAccountInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return '-';
    // 문자열로 온 경우 앞의 0을 제거하고 숫자로 변환
    const numValue = typeof value === 'string' ? parseInt(value.replace(/^0+/, '') || '0') : parseInt(value || 0);
    return numValue.toLocaleString();
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };




  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      </Box>

      {errorMessage && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => window.location.href = '/settings'}
            >
              환경설정
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {holdingsError && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          onClose={() => setHoldingsError(null)}
        >
          보유 종목 조회: {holdingsError}
        </Alert>
      )}

      {loading ? (
          <Card>
          <CardContent>
            <Typography>로딩 중...</Typography>
          </CardContent>
        </Card>
      ) : accountInfo ? (
        <Box>
          {/* 상단 요약 정보 - 이미지 스타일 */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                실시간 잔고
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {accountInfo.tot_pur_amt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          총매입금액
                        </TableCell>
                      )}
                      {accountInfo.tot_evlt_amt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          총평가금액
                        </TableCell>
                      )}
                      {accountInfo.tot_evlt_pl !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          총평가손익
                        </TableCell>
                      )}
                      {accountInfo.tot_prft_rt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          수익률(%)
                        </TableCell>
                      )}
                      {accountInfo.entr !== undefined && accountInfo.entr !== null && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          예수금
                        </TableCell>
                      )}
                      {accountInfo.prsm_dpst_aset_amt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          추정자산
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {accountInfo.tot_pur_amt !== undefined && (
                        <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                          {formatNumber(accountInfo.tot_pur_amt)}
                        </TableCell>
                      )}
                      {accountInfo.tot_evlt_amt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          {formatNumber(accountInfo.tot_evlt_amt)}
                        </TableCell>
                      )}
                      {accountInfo.tot_evlt_pl !== undefined && (
                        <TableCell 
                          align="center" 
                          sx={{ 
                            color: parseInt(accountInfo.tot_evlt_pl || 0) >= 0 ? '#d32f2f' : '#1976d2',
                            fontWeight: 'bold',
                            borderRight: '1px solid', 
                            borderColor: 'divider'
                          }}
                        >
                          {formatNumber(accountInfo.tot_evlt_pl)}
                        </TableCell>
                      )}
                      {accountInfo.tot_prft_rt !== undefined && (
                        <TableCell 
                          align="center" 
                          sx={{ 
                            color: parseFloat(accountInfo.tot_prft_rt || 0) >= 0 ? '#d32f2f' : '#1976d2',
                            fontWeight: 'bold',
                            borderRight: '1px solid', 
                            borderColor: 'divider'
                          }}
                        >
                          {formatPercent(accountInfo.tot_prft_rt)}
                        </TableCell>
                      )}
                      {accountInfo.entr !== undefined && accountInfo.entr !== null && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main', borderRight: '1px solid', borderColor: 'divider' }}>
                          {formatNumber(accountInfo.entr)}
                        </TableCell>
                      )}
                      {accountInfo.prsm_dpst_aset_amt !== undefined && (
                        <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {formatNumber(accountInfo.prsm_dpst_aset_amt)}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* 보유 종목 정보 */}
          {(() => {
            // kt00004 응답에서 보유 종목 데이터 추출
            // kt00004(계좌평가현황요청)는 계좌 평가 현황과 함께 보유 종목 정보를 제공
            // accountInfo.holdings는 이미 백엔드에서 파싱된 데이터이거나 원본 응답일 수 있음
            let holdings = null;
            
            // 1차: accountInfo.holdings가 배열인 경우
            if (Array.isArray(accountInfo.holdings)) {
              holdings = accountInfo.holdings;
            }
            // 2차: accountInfo.holdings가 객체인 경우, 내부 필드 확인
            else if (accountInfo.holdings && typeof accountInfo.holdings === 'object') {
              // kt00004의 보유 종목 필드들 (다양한 가능성 확인)
              holdings = accountInfo.holdings.stk_acnt_evlt_prst || // 주식계좌평가현황 (보유 종목 배열)
                        accountInfo.holdings.output || 
                        accountInfo.holdings.output1 || 
                        accountInfo.holdings.acnt_evlt_remn_indv_tot || // 계좌평가잔고개별합계
                        accountInfo.holdings.stk_cntr_remn || // kt00005에서 사용하는 필드 (혹시 모를 경우)
                        accountInfo.holdings.data ||
                        accountInfo.holdings.holdings;
            }
            // 3차: accountInfo 최상위 레벨에서 직접 찾기 (kt00004 응답 구조)
            if (!holdings) {
              holdings = accountInfo.stk_acnt_evlt_prst || // 주식계좌평가현황 (보유 종목 배열)
                        accountInfo.output || 
                        accountInfo.output1 || 
                        accountInfo.acnt_evlt_remn_indv_tot || // 계좌평가잔고개별합계
                        accountInfo.stk_cntr_remn || // kt00005에서 사용하는 필드 (혹시 모를 경우)
                        accountInfo.data ||
                        accountInfo.holdings;
            }
            
            console.log('보유 종목 원본 데이터:', holdings);
            console.log('보유 종목 타입:', typeof holdings);
            console.log('배열 여부:', Array.isArray(holdings));
            if (holdings && typeof holdings === 'object' && !Array.isArray(holdings)) {
              console.log('보유 종목 객체 키들:', Object.keys(holdings));
            }
            
            // 데이터가 없으면 표시하지 않음
            if (!holdings) {
              console.log('보유 종목 데이터가 없습니다.');
              return null;
            }
            
            // 배열 처리
            let flatHoldings = [];
            if (Array.isArray(holdings)) {
              console.log('holdings는 배열입니다. 길이:', holdings.length);
              // 배열 안에 배열이 있을 수 있으므로 평탄화
              flatHoldings = holdings.flat();
              console.log('평탄화 후 길이:', flatHoldings.length);
              
              // 배열 안의 각 요소가 객체인지 확인하고, 종목코드나 종목명이 있는 것만 필터링
              flatHoldings = flatHoldings.filter(item => {
                if (!item || typeof item !== 'object') {
                  console.log('유효하지 않은 항목:', item);
                  return false;
                }
                // 종목코드 또는 종목명 필드가 있는지 확인 (다양한 필드명 지원)
                // 실제 API 응답 필드명: stk_cd, stk_nm
                const hasStockCode = item.stk_cd || item.pdno || item.itm_cd || item.stock_code || item.code || item.srtn_cd;
                const hasStockName = item.stk_nm || item.pdno_nm || item.itm_nm || item.prdt_name || item.stock_name || item.itm_nm_eng || item.prdt_nm || item.itm_nm_kor;
                const isValid = hasStockCode || hasStockName;
                if (!isValid) {
                  console.log('종목코드/종목명이 없는 항목:', item);
                  console.log('항목의 키들:', Object.keys(item));
                }
                return isValid;
              });
              console.log('필터링 후 보유 종목 개수:', flatHoldings.length);
            } else if (typeof holdings === 'object' && holdings !== null) {
              console.log('holdings는 객체입니다. 키들:', Object.keys(holdings));
              // 객체인 경우 배열로 변환 시도
              // 단일 객체인 경우
              // 실제 API 응답 필드명: stk_cd, stk_nm
              const hasStockCode = holdings.stk_cd || holdings.pdno || holdings.itm_cd || holdings.stock_code || holdings.code || holdings.srtn_cd;
              const hasStockName = holdings.stk_nm || holdings.pdno_nm || holdings.itm_nm || holdings.prdt_name || holdings.stock_name || holdings.itm_nm_eng || holdings.prdt_nm || holdings.itm_nm_kor;
              if (hasStockCode || hasStockName) {
                flatHoldings = [holdings];
                console.log('단일 보유 종목 객체로 처리');
              } else {
                // 객체의 값들이 배열인 경우
                const values = Object.values(holdings);
                console.log('객체의 값들:', values);
                flatHoldings = values.filter(item => {
                  if (!item || typeof item !== 'object') return false;
                  const hasCode = item.stk_cd || item.pdno || item.itm_cd || item.stock_code || item.code || item.srtn_cd;
                  const hasName = item.stk_nm || item.pdno_nm || item.itm_nm || item.prdt_name || item.stock_name || item.itm_nm_eng || item.prdt_nm || item.itm_nm_kor;
                  return hasCode || hasName;
                });
                console.log('객체 값에서 필터링된 보유 종목 개수:', flatHoldings.length);
              }
            } else {
              console.log('holdings가 null이거나 유효하지 않습니다:', holdings);
            }
            
            console.log('평탄화된 보유 종목:', flatHoldings);
            console.log('보유 종목 개수:', flatHoldings.length);
            
            if (flatHoldings.length === 0) {
              console.log('표시할 보유 종목이 없습니다.');
              return (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                      보유 종목
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      보유 종목이 없습니다.
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            return (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  보유 종목
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          종목명
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          수량
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          매입가
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          현재가
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          평가손익
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          수익률
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid', borderColor: 'divider' }}>
                          매입금액
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          평가금액
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flatHoldings.map((holding, index) => {
                        console.log(`보유 종목 ${index}:`, holding);
                        console.log(`보유 종목 ${index} 필드들:`, Object.keys(holding));
                        
                        // 실제 필드명 확인 및 매핑 (kt00004 응답 필드명)
                        // kt00004의 stk_acnt_evlt_prst 필드 구조에 맞춘 필드명 매핑
                        // 실제 API 응답 필드명: stk_cd, stk_nm, rmnd_qty, avg_prc, cur_prc, pl_amt, pl_rt, pur_amt, evlt_amt
                        const stockCode = holding.stk_cd || holding.pdno || holding.itm_cd || holding.stock_code || holding.code || holding.srtn_cd || '';
                        const stockName = holding.stk_nm || holding.itm_nm || holding.pdno_nm || holding.prdt_name || holding.stock_name || holding.itm_nm_eng || holding.prdt_nm || holding.itm_nm_kor || stockCode || '-';
                        const quantity = holding.rmnd_qty || holding.hldg_qty || holding.qty || holding.quantity || holding.ord_qty || holding.hldg_qty_remn || 0;
                        const purchasePrice = holding.avg_prc || holding.pchs_avg_pric || holding.purchase_price || holding.avg_pric || holding.pchs_avg_pric_remn || 0;
                        const currentPrice = holding.cur_prc || holding.prpr || holding.current_price || holding.now_prc || holding.prc || holding.prpr_remn || 0;
                        const profitLoss = holding.pl_amt || holding.evlu_pfls_amt || holding.profit_loss || holding.evlu_pfls || holding.pfls_amt || holding.evlu_pfls_amt_remn || 0;
                        const profitRate = holding.pl_rt || holding.evlu_pfls_rt || holding.profit_rate || holding.pfls_rt || holding.rate || holding.evlu_pfls_rt_remn || 0;
                        const purchaseAmount = holding.pur_amt || holding.pchs_amt || holding.purchase_amount || holding.buy_amt || holding.pchs_amt_remn || 0;
                        const evaluationAmount = holding.evlt_amt || holding.evlu_amt || holding.evaluation_amount || holding.eval_amt || holding.evlu_amt_remn || 0;
                        
                        return (
                        <TableRow key={index} hover>
                          <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                            <Box>
                              <Typography variant="body2">{stockName}</Typography>
                              {stockCode && stockCode !== stockName && (
                                <Typography variant="caption" color="text.secondary">
                                  ({stockCode})
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                            {formatNumber(quantity)}
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                            {formatNumber(purchasePrice)}
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                            {formatNumber(currentPrice)}
                          </TableCell>
                          <TableCell 
                            align="center" 
                            sx={{ 
                              borderRight: '1px solid', 
                              borderColor: 'divider',
                              color: parseFloat(profitLoss || 0) >= 0 ? '#d32f2f' : '#1976d2',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatNumber(profitLoss)}
                          </TableCell>
                          <TableCell 
                            align="center" 
                            sx={{ 
                              borderRight: '1px solid', 
                              borderColor: 'divider',
                              color: parseFloat(profitRate || 0) >= 0 ? '#d32f2f' : '#1976d2',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatPercent(profitRate)}
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                            {formatNumber(purchaseAmount)}
                          </TableCell>
                          <TableCell align="center">
                            {formatNumber(evaluationAmount)}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
            );
          })()}

                </Box>
              ) : (
        <Card>
          <CardContent>
            <Typography color="error" gutterBottom>
                  계좌 정보를 불러올 수 없습니다.
                </Typography>
            {errorMessage && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {errorMessage}
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => window.location.href = '/settings'}
                  >
                    환경설정으로 이동
                  </Button>
                </Box>
              </Alert>
              )}
            </CardContent>
          </Card>
      )}
    </Container>
  );
};

export default Account;
